import {resolveExchAcid, resolvePh} from './limeSoilUtils'
import LabInputRow from '../../controllers/LabInputRow'
import TemplateData from '../../controllers/TemplateData'
import LimePlayFieldRow from '../../controllers/LimePlayFieldRow'
import LimeProgramLabInputRow from '../../controllers/LimeProgramLabInputRow'
import LimeCalculatedOutputs from '../../controllers/LimeCalculatedOutputs'
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs'
import LimeProductResponse from '../../controllers/LimeProductResponse'
import GypsumPurpose from '../../controllers/GypsumPurpose'
import BalancingNormBasis from '../../controllers/BalancingNormBasis'
import KRecommendationBasis from '../../controllers/KRecommendationBasis'
import PAnalysisMethod from '../../controllers/PAnalysisMethod'
import PhosphorusParameters from '../../controllers/PhosphorusParameters'
import SulphurTargetPh from '../../controllers/SulphurTargetPh'
import { ELEMENT_SPECIFIC_GRAVITY, BASE_SATURATION_BY_CEC_STATIC, BASE_SATURATION_BY_DENSITY_STATIC, ELEMENTAL_SULPHUR_BASE_RATES } from './staticdata'

// Re-export LimePlayFieldRow as LabInfoRow for backward compatibility
export type LabInfoRow = LimePlayFieldRow

// Lime type enum
export type LimeType = 'Calcitic' | 'Dolomitic'

// ============================================================================
// Product Values - built from actual DB products or defaults
// ============================================================================

export interface ProductValues {
    calcitic: { caPercent: number; mgPercent: number; cce: number; sPercent?: number; kPercent?: number; pPercent?: number }
    dolomitic: { caPercent: number; mgPercent: number; cce: number; sPercent?: number; kPercent?: number; pPercent?: number }
    magnesium: { mgPercent: number; cce: number; caPercent?: number }
    gypsum: { caPercent: number; sPercent: number; cce: number; kPercent?: number; pPercent?: number }
    potassium: { kPercent: number; caPercent?: number }
    phosphate: { pPercent: number; caPercent?: number }
}

// Default product values (from Excel - used when no products selected)
export const DEFAULT_PRODUCT_VALUES: ProductValues = {
    calcitic: {
        caPercent: 0.34,      // 34% Ca in calcitic lime
        mgPercent: 0.003,     // 0.3% Mg in calcitic lime
        cce: 0.82             // 82% CCE (purity)
    },
    dolomitic: {
        caPercent: 0.236,     // 23.6% Ca in dolomitic lime
        mgPercent: 0.111,     // 11.1% Mg in dolomitic lime
        cce: 0.95             // 95% CCE (purity)
    },
    magnesium: {
        // TODO(magnesium): confirm default Mg product composition with Schalk.
        mgPercent: 0.25,      // ~25% Mg (placeholder, e.g. magnesite MgCO3)
        cce: 0.90             // 90% CCE (placeholder)
    },
    gypsum: {
        caPercent: 0.2326,    // 23.26% Ca in gypsum
        sPercent: 0.18,       // 18% S in gypsum
        cce: 0.95             // 95% CCE
    },
    potassium: {
        kPercent: 0.4         // K2SO4 = 40% K
    },
    phosphate: {
        pPercent: 0.22        // MAP = 22% P
    }
}

/**
 * Build ProductValues from actual LimeProductResponse objects.
 * FIX 3: Use actual product compositions from database instead of hardcoded defaults.
 * LimeProductResponse stores percentages as decimals (0.34 = 34%).
 */
export function buildProductValues(
    calciticProduct: LimeProductResponse | null | undefined,
    dolomiticProduct: LimeProductResponse | null | undefined,
    magnesiumProduct: LimeProductResponse | null | undefined,
    gypsumProduct: LimeProductResponse | null | undefined,
    potassiumProduct: LimeProductResponse | null | undefined,
    phosphateProduct: LimeProductResponse | null | undefined
): ProductValues {
    return {
        calcitic: calciticProduct ? {
            caPercent: calciticProduct.caPercent,
            mgPercent: calciticProduct.mgPercent,
            cce: calciticProduct.ccePercent,
            sPercent: calciticProduct.sPercent,
            kPercent: calciticProduct.kPercent,
            pPercent: calciticProduct.pPercent,
        } : DEFAULT_PRODUCT_VALUES.calcitic,
        dolomitic: dolomiticProduct ? {
            caPercent: dolomiticProduct.caPercent,
            mgPercent: dolomiticProduct.mgPercent,
            cce: dolomiticProduct.ccePercent,
            sPercent: dolomiticProduct.sPercent,
            kPercent: dolomiticProduct.kPercent,
            pPercent: dolomiticProduct.pPercent,
        } : DEFAULT_PRODUCT_VALUES.dolomitic,
        magnesium: magnesiumProduct ? {
            mgPercent: magnesiumProduct.mgPercent,
            cce: magnesiumProduct.ccePercent,
            caPercent: magnesiumProduct.caPercent,
        } : DEFAULT_PRODUCT_VALUES.magnesium,
        gypsum: gypsumProduct ? {
            caPercent: gypsumProduct.caPercent,
            sPercent: gypsumProduct.sPercent,
            cce: gypsumProduct.ccePercent,
            kPercent: gypsumProduct.kPercent,
            pPercent: gypsumProduct.pPercent,
        } : DEFAULT_PRODUCT_VALUES.gypsum,
        potassium: potassiumProduct ? {
            kPercent: potassiumProduct.kPercent,
            caPercent: potassiumProduct.caPercent,
        } : DEFAULT_PRODUCT_VALUES.potassium,
        phosphate: phosphateProduct ? {
            pPercent: phosphateProduct.pPercent,
            caPercent: phosphateProduct.caPercent,
        } : DEFAULT_PRODUCT_VALUES.phosphate,
    }
}

// ============================================================================
// Calculation Options
// ============================================================================

export interface CalcOptions {
    /** Lime/gypsum/Mg depth (cm) from the program master tab. Defaults to 30. */
    limeGypsumDepthCm?: number
    /** P/K depth (cm) from the program master tab. Falls back to limeGypsumDepthCm. */
    pkDepthCm?: number
    /** Product compositions. Falls back to DEFAULT_PRODUCT_VALUES */
    products?: ProductValues
    /**
     * Which products are actually selected on the master tab. A false entry means the product
     * is NULL/None → that strategy is skipped (need + Y1/Y2 = 0, rendered as "-").
     * Absent → treat all as selected (backwards-compatible current behaviour).
     */
    productSelected?: {
        calcitic: boolean
        dolomitic: boolean
        magnesium: boolean
        gypsum: boolean
        potassium: boolean
        phosphate: boolean
    }
    /**
     * P analysis method selected on the master tab. Determines which imported P value is used
     * and which corresponding template ideal-P parameter drives the P calculation.
     * Absent → Bray I (historical single-analysis behaviour).
     */
    pAnalysisMethod?: PAnalysisMethod
}

/** Resolve the soil P value (mg/kg) for the selected analysis method from a calc row. */
export function selectPValue(row: LabInputRow, method: PAnalysisMethod): number {
    switch (method) {
        case PAnalysisMethod.BrayII: return row.pBrayII
        case PAnalysisMethod.Olsen: return row.pOlsen
        case PAnalysisMethod.MehlichIII: return row.pMehlichIII
        case PAnalysisMethod.BrayI:
        default: return row.pBrayI
    }
}

/**
 * Resolve the ideal P (mg/kg) template parameter for the selected analysis method. Each method has
 * its own independent ideal (their agronomic target ranges differ) — there is deliberately no
 * cross-method fallback. Bray I's ideal is stored under the legacy IdealMgKg name.
 */
export function selectIdealP(phosphorus: PhosphorusParameters, method: PAnalysisMethod): number {
    switch (method) {
        case PAnalysisMethod.BrayII: return phosphorus.idealPBrayIIMgKg
        case PAnalysisMethod.Olsen: return phosphorus.idealPOlsenMgKg
        case PAnalysisMethod.MehlichIII: return phosphorus.idealPMehlichIIIMgKg
        case PAnalysisMethod.BrayI:
        default: return phosphorus.idealMgKg
    }
}

// ============================================================================
// Fixed K% break point thresholds (DO NOT EDIT in Excel - no color)
// ============================================================================
const K_PERCENT_THRESHOLDS = [0.02, 0.03, 0.04, 0.05, 0.06, 0.07, 0.08]

// Element type for specific gravity lookup
type Element = keyof typeof ELEMENT_SPECIFIC_GRAVITY;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert element from mg/kg to cmolc/kg
 * Formula: value (mg/kg) / specific gravity
 */
export function mgKgToCmolc(value: number, element: Element): number {
    return value / ELEMENT_SPECIFIC_GRAVITY[element]
}

/**
 * Convert mg/kg to kg/ha at specified depth
 * Formula: value (mg/kg) * density * (depthCm / 10)
 */
export function mgKgToKgHa(value: number, density: number, depthCm: number): number {
    return value * density * (depthCm / 10)
}

/**
 * Calculate ratio between two values (safe division)
 * Returns 0 if divisor is 0
 */
export function ratio(numerator: number, divisor: number): number {
    if (divisor === 0) return 0
    return numerator / divisor
}

// ============================================================================
// Lookup Functions
// ============================================================================

/**
 * Lookup CEC operator from CEC value using VLOOKUP-style lookup
 * Excel: =VLOOKUP(V19,Parameters!$E$47:$F$59,2,TRUE)
 * Finds the largest operatingCec value that is <= the input cec
 */
export function lookupCecOperator(cec: number): number {
    let result = BASE_SATURATION_BY_CEC_STATIC[0]!
    for (const entry of BASE_SATURATION_BY_CEC_STATIC) {
        if (entry.operatingCec <= cec) {
            result = entry
        } else {
            break
        }
    }
    return result.operator
}

/**
 * Lookup Density operator from density value using VLOOKUP-style lookup
 * Excel: =VLOOKUP(K19,Parameters!$E$35:$F$43,2,TRUE)
 * Finds the largest operatingDensity value that is <= the input density
 */
export function lookupDensityOperator(density: number): number {
    let result = 1 // Default operator
    for (const entry of BASE_SATURATION_BY_DENSITY_STATIC) {
        if (entry.operatingDensity <= density) {
            result = BASE_SATURATION_BY_DENSITY_STATIC.indexOf(entry) + 1
        } else {
            break
        }
    }
    return result
}

/**
 * Get base saturation norms by CEC from template rules.
 * Excel: When Parameters!$N$46 = "CEC", uses CEC-based norms.
 * Returns norms in the same shape as density-based norms (ca, mg, k, caMgRatio).
 */
export function getBaseSaturationNormsByCec(cec: number, template: TemplateData) {
    const rules = template.baseSaturation.baseSaturationByCec
    const entries = [
        rules.cec0, rules.cec1, rules.cec2, rules.cec4, rules.cec6, rules.cec8,
        rules.cec10, rules.cec12, rules.cec14, rules.cec16, rules.cec18, rules.cec20, rules.cec22
    ].sort((a, b) => a.operatingCec - b.operatingCec)

    let match = entries[0]!
    for (const entry of entries) {
        if (entry.operatingCec <= cec) {
            match = entry
        } else {
            break
        }
    }

    return {
        ca: match.targetCa,
        mg: match.targetMg,
        k: match.targetK,
        caMgRatio: match.caMgRatio
    }
}

/**
 * FIX 12: Get base saturation norms by density from template rules.
 * Excel: When Parameters!$N$46 = "Density", uses density-based norms.
 * Returns norms in the same shape as CEC-based norms (ca, mg, k, caMgRatio).
 */
export function getBaseSaturationNormsByDensity(density: number, template: TemplateData) {
    const rules = template.baseSaturation.baseSaturationByDensity
    const entries = [
        rules.density0, rules.density110, rules.density115, rules.density120,
        rules.density125, rules.density130, rules.density135, rules.density140, rules.density145
    ].sort((a, b) => a.operatingDensity - b.operatingDensity)

    let match = entries[0]!
    for (const entry of entries) {
        if (entry.operatingDensity <= density) {
            match = entry
        } else {
            break
        }
    }

    const caMgRatio = match.targetMg > 0 ? match.targetCa / match.targetMg : 0
    return {
        ca: match.targetCa,
        mg: match.targetMg,
        k: match.targetK,
        caMgRatio
    }
}

/**
 * Calculate nutrient level at norm (mg/kg)
 * Formula: normPercent * CEC * specific_gravity
 * Excel: =$AF19*V19*SGCa (e.g., Ca norm% * CEC * 200)
 */
export function calcNutrientLevelAtNorm(normPercent: number, cec: number, element: Element): number {
    return normPercent * cec * ELEMENT_SPECIFIC_GRAVITY[element]
}

/**
 * Lookup depth multiplier factor from density operator and depth
 * Excel: =VLOOKUP(AE19,Parameters!$F$35:$Q$43,VLOOKUP($AV$8,Parameters!$AV$21:$AW$28,2,FALSE),FALSE)
 */
export function lookupDepthFactor(densityOperator: number, depthCm: number): number {
    const entry = BASE_SATURATION_BY_DENSITY_STATIC[densityOperator - 1]
    if (!entry) return 3.3 // Default for 30cm

    const depthKey = `depth${depthCm}` as keyof typeof entry.depthMultipliers
    return entry.depthMultipliers[depthKey] ?? 3.3
}

// ============================================================================
// Lime Type Determination
// ============================================================================

/**
 * FIX 4: Determine lime type based on Excel's full priority order.
 * Excel (AP19 → AR19 → AT19):
 *   1. If reachIdealCa=Yes: Ca < idealCa → "FALSE" (Calcitic)
 *   2. If reachIdealMg=Yes: Mg < idealMg → "TRUE" (Dolomitic), else check Mg% < norm Mg%
 *   3. Default: if Mg% < norm Mg% → "TRUE" (Dolomitic)
 *   4. If activateBreak=Yes, check Mg ppm against dolomitic rules table (CEC-based threshold)
 * AR19: =IF(AP19="FALSE","Calcitic lime","Dolomitic lime")
 * AT19: =IF(AS19<>"",AS19,AR19)  [AS19 is override]
 */
export function determineLimeType(
    caDiffMgKg: number,
    mgDiffMgKg: number,
    cec: number,
    mgMgKg: number,
    caMgKg: number,
    mgPercent: number,
    mgPercentNorm: number,
    template: TemplateData
): LimeType {
    // Step 1: If reachIdealCa is active
    if (template.lime.reachIdealCa) {
        // Ca < idealCa → lime rule = FALSE → Calcitic
        if (caMgKg < template.lime.idealCaMgKg) {
            return 'Calcitic'
        }
        return 'Dolomitic'
    }

    // Step 2: If reachIdealMg is active
    if (template.lime.reachIdealMg) {
        // Mg < idealMg → lime rule = TRUE → Dolomitic
        if (mgMgKg < template.lime.idealMgMgKg) {
            return 'Dolomitic'
        }
        // Else check Mg% < norm
        if (mgPercent < mgPercentNorm) {
            return 'Dolomitic'
        }
        return 'Calcitic'
    }

    // Step 3: Default path - check Mg% vs norm
    if (mgPercent < mgPercentNorm) {
        return 'Dolomitic'
    }

    // Step 4: If activate break = Yes, check dolomitic rules table
    if (template.lime.breakActivated) {
        const dolomiticRules = template.lime.dolomiticRules
        const cecEntries = [
            dolomiticRules.cec0, dolomiticRules.cec1, dolomiticRules.cec2, dolomiticRules.cec2_5,
            dolomiticRules.cec3, dolomiticRules.cec4, dolomiticRules.cec5, dolomiticRules.cec6,
            dolomiticRules.cec7, dolomiticRules.cec8, dolomiticRules.cec9, dolomiticRules.cec10,
            dolomiticRules.cec11, dolomiticRules.cec12, dolomiticRules.cec13, dolomiticRules.cec14,
            dolomiticRules.cec15, dolomiticRules.cec16, dolomiticRules.cec17, dolomiticRules.cec18,
            dolomiticRules.cec19, dolomiticRules.cec20, dolomiticRules.cec21, dolomiticRules.cec22,
            dolomiticRules.cec23, dolomiticRules.cec24, dolomiticRules.cec25, dolomiticRules.cec26,
            dolomiticRules.cec27, dolomiticRules.cec28, dolomiticRules.cec29, dolomiticRules.cec30
        ].sort((a, b) => a.cec - b.cec)

        let mgThreshold = cecEntries[0]?.mgThresholdPpm ?? 60
        for (const entry of cecEntries) {
            if (entry.cec <= cec) {
                mgThreshold = entry.mgThresholdPpm
            }
        }

        // Mg < threshold → Dolomitic
        if (mgMgKg < mgThreshold) {
            return 'Dolomitic'
        }
    }

    return 'Calcitic'
}

// ============================================================================
// Lime Rule (is lime needed?)
// ============================================================================

/**
 * Determine if lime is needed (Lime Rule)
 * Excel AP19: Complex IF logic checking reachIdealCa, reachIdealMg, Mg%, and acid neutralization
 */
export function calcLimeRule(
    caPercent: number,
    caPercentNorm: number,
    mgPercent: number,
    mgPercentNorm: number,
    mgMgKg: number,
    caMgKg: number,
    exchH: number,
    ph: number,
    template: TemplateData
): boolean {
    // Check reachIdealCa first
    if (template.lime.reachIdealCa) {
        return caMgKg < template.lime.idealCaMgKg
    }

    // Check reachIdealMg
    if (template.lime.reachIdealMg) {
        if (mgMgKg < template.lime.idealMgMgKg) return true
        return mgPercent < mgPercentNorm
    }

    // Default: check Mg% vs norm (which determines dolomitic)
    if (mgPercent < mgPercentNorm) return true

    // Check for acid neutralization
    if (template.lime.neutraliseAcid && exchH > 0) {
        return true
    }

    // Check pH-based lime need
    if (template.lime.standardByPh && ph < template.lime.desiredPh) {
        return true
    }

    return false
}

// ============================================================================
// Amendment Supply Functions
// ============================================================================

/**
 * Calculate Ca amendment supply (kg/ha)
 * Excel AU19: Ca deficit * depthFactor / product Ca% / CCE
 */
export function calcCaAmendmentSupply(
    caDiffMgKg: number,
    depthFactor: number,
    limeType: LimeType,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    const product = limeType === 'Calcitic' ? products.calcitic : products.dolomitic
    const caPercent = product.caPercent
    const cce = product.cce

    // caPercent === 0 / cce === 0 mirror the Excel AU19 IFERROR(...) which returns blank (0)
    // on a divide-by-zero, so a product supplying no Ca or no neutralising value (CCE)
    // contributes no amendment — and this stops Infinity/NaN from poisoning the amendment-need MAX.
    if (caDiffMgKg >= 0 || caPercent === 0 || cce === 0) return 0 // No deficit / no valid product
    const amountNeeded = Math.abs(caDiffMgKg) * depthFactor / caPercent / cce
    return Math.max(0, amountNeeded)
}

/**
 * Calculate Mg amendment supply (kg/ha)
 * Excel AV19: Mg deficit * depthFactor / product Mg% / CCE
 */
export function calcMgAmendmentSupply(
    mgDiffMgKg: number,
    depthFactor: number,
    limeType: LimeType,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    const product = limeType === 'Calcitic' ? products.calcitic : products.dolomitic
    const mgPercent = product.mgPercent
    const cce = product.cce

    // Same divide-by-zero guard as calcCaAmendmentSupply: a product with 0 Mg% or 0 CCE
    // contributes no amendment (avoids Infinity/NaN poisoning the amendment-need MAX).
    if (mgDiffMgKg >= 0 || mgPercent === 0 || cce === 0) return 0
    const amountNeeded = Math.abs(mgDiffMgKg) * depthFactor / mgPercent / cce
    return Math.max(0, amountNeeded)
}

/**
 * Calculate Mg supply for ideal Mg target (kg/ha)
 * Excel AW19: =IF(Parameters!$Y$14="Yes",((Parameters!$Y$15-H19)/$AX$3*AQ19),0)
 */
export function calcMgIdealSupply(
    mgMgKg: number,
    idealMgMgKg: number,
    depthFactor: number,
    reachIdealMg: boolean,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    if (!reachIdealMg) return 0
    const deficit = idealMgMgKg - mgMgKg
    if (deficit <= 0) return 0
    return deficit / products.dolomitic.mgPercent * depthFactor
}

/**
 * Calculate Ca supply for ideal Ca target (kg/ha)
 * Excel AX19: =(Parameters!$Y$18-G19)*AQ19/IF(AT19="Calcitic lime",BronPersCa,BronPersMg)
 */
export function calcCaIdealSupply(
    caMgKg: number,
    idealCaMgKg: number,
    depthFactor: number,
    reachIdealCa: boolean,
    limeType: LimeType,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    if (!reachIdealCa) return 0
    const deficit = idealCaMgKg - caMgKg
    if (deficit <= 0) return 0
    const product = limeType === 'Calcitic' ? products.calcitic : products.dolomitic
    // Excel AX19 is not IFERROR-wrapped and would show #DIV/0! at Ca% = 0; we
    // return 0 instead so a 0 Ca% product simply contributes no ideal-Ca supply.
    if (product.caPercent === 0) return 0
    return deficit * depthFactor / product.caPercent
}

// ============================================================================
// Magnesium product supply (dolomitic Mg formula, minus Ca/pH, using the Mg product)
// ============================================================================

/**
 * Magnesium product amendment supply (kg/ha).
 * Mirrors the dolomitic Mg supply (calcMgAmendmentSupply) but always uses the dedicated
 * Magnesium product and ignores lime type — the Magnesium formula is "the dolomitic formula
 * without the Ca/pH terms". Same divide-by-zero guards.
 */
export function calcMagnesiumProductSupply(
    mgDiffMgKg: number,
    depthFactor: number,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    const mgPercent = products.magnesium.mgPercent
    const cce = products.magnesium.cce
    if (mgDiffMgKg >= 0 || mgPercent === 0 || cce === 0) return 0
    return Math.max(0, Math.abs(mgDiffMgKg) * depthFactor / mgPercent / cce)
}

/**
 * Magnesium product ideal-Mg supply (kg/ha).
 * Mirrors calcMgIdealSupply but uses the Magnesium product (no CCE divisor, matching the
 * dolomitic ideal-Mg formula).
 */
export function calcMagnesiumProductIdealSupply(
    mgMgKg: number,
    idealMgMgKg: number,
    depthFactor: number,
    reachIdealMg: boolean,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    if (!reachIdealMg) return 0
    const deficit = idealMgMgKg - mgMgKg
    if (deficit <= 0) return 0
    const mgPercent = products.magnesium.mgPercent
    if (mgPercent === 0) return 0
    return deficit / mgPercent * depthFactor
}

// ============================================================================
// FIX 1: pH-based Lime Calculation
// ============================================================================

/**
 * Calculate pH-based lime need (kg/ha).
 * Excel AY19: =(desiredPh - currentPh) * VLOOKUP(CEC, bufferTable, 3, TRUE) * depth/30 / CCE
 * Gated behind template.lime.standardByPh flag.
 * Buffer constants come from template (CAN EDIT / yellow in Excel).
 */
export function calcPhBasedLimeNeed(
    currentPh: number,
    desiredPh: number,
    cec: number,
    limeType: LimeType,
    depthCm: number,
    template: TemplateData,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    if (!template.lime.standardByPh) return 0
    if (currentPh >= desiredPh) return 0

    // Lookup buffer factor from template's soil buffering constants
    const constants = template.lime.soilBufferingConstants
    if (!constants) return 0

    const entries = [
        constants.cec0, constants.cec5, constants.cec10, constants.cec15
    ].sort((a, b) => a.cec - b.cec)

    let bufferFactor = entries[0]?.bufferFactor ?? 2000
    for (const entry of entries) {
        if (entry.cec <= cec) {
            bufferFactor = entry.bufferFactor
        }
    }

    const cce = limeType === 'Calcitic' ? products.calcitic.cce : products.dolomitic.cce
    return (desiredPh - currentPh) * bufferFactor * (depthCm / 30) / cce
}

// ============================================================================
// FIX 2: Exchangeable Acid Neutralization
// ============================================================================

/**
 * Calculate exchangeable acid lime need (kg/ha).
 * Excel AZ19: Only when Suur (neutraliseAcid) = "YES"
 *
 * Formula:
 *   (mgPart + caPart) / productTotal / CCE
 *
 * Where:
 *   mgPart = exchH × (Mg%/total%) × SGMg × depthFactor
 *   caPart = exchH × (Ca%/total%) × SGCa × depthFactor
 *   productTotal = selected lime product's (Ca% + Mg%)
 *   CCE = selected lime type's CCE
 */
export function calcExchAcidLimeNeed(
    exchH: number,
    limeType: LimeType,
    depthFactor: number,
    neutraliseAcid: boolean,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    if (!neutraliseAcid || exchH <= 0) return 0

    const product = limeType === 'Calcitic' ? products.calcitic : products.dolomitic
    const caP = product.caPercent
    const mgP = product.mgPercent
    const cce = product.cce
    const totalP = caP + mgP

    if (totalP === 0 || cce === 0) return 0

    const mgFraction = mgP / totalP
    const caFraction = caP / totalP

    const mgPart = (exchH * mgFraction) * ELEMENT_SPECIFIC_GRAVITY.Mg * depthFactor
    const caPart = (exchH * caFraction) * ELEMENT_SPECIFIC_GRAVITY.Ca * depthFactor

    return (mgPart + caPart) / totalP / cce
}

// ============================================================================
// Amendment Need (MAX of all calculations)
// ============================================================================

/**
 * Calculate amendment need (maximum of various calculations)
 * Excel BA19: =MAX(
 *   IF(AT19="Calcitic lime",AU19,0),
 *   IF(AT19="Dolomitic lime",IF($BA$15="Mg",AV19,AU19),0),
 *   AW19, AX19, AY19,
 *   IF(Parameters!$AD$21="YES",AY19,0)
 * )
 * When Calcitic: only Ca supply participates (not Mg supply)
 * When Dolomitic: only Mg supply participates (not Ca supply)
 * Exchangeable-acid lime (Excel AZ19) IS included here, gated by the neutraliseAcid
 * flag. This is a deliberate step beyond the Excel: in the workbook AZ19 only feeds
 * display stats and is not part of the BA19 MAX. 
 */
export function calcAmendmentNeed(
    caSupply: number,
    mgSupply: number,
    mgIdealSupply: number,
    caIdealSupply: number,
    phBasedLime: number = 0,
    exchAcidLime: number = 0,
    limeType: LimeType = 'Dolomitic'
): number {
    // Excel conditionally includes Ca or Mg supply based on lime type
    const supplyForType = limeType === 'Calcitic' ? caSupply : mgSupply
    return Math.max(supplyForType, mgIdealSupply, caIdealSupply, phBasedLime, exchAcidLime, 0)
}

// ============================================================================
// Max Application Lookups
// ============================================================================

/**
 * Get max lime application based on CEC
 * Excel: VLOOKUP(BD19, Parameters!$AC$13:$AD$19,2,TRUE)
 *
 * Excel BF19 (year 1) applies this cap only when the max-break toggle (Parameters!$AD$11)
 * is on, but BG19/BH19 (years 2/3) cap unconditionally. Pass alwaysCap=true to ignore the
 * toggle for the year-2/3 calculations. 
 */
export function getMaxLimeApplication(cec: number, template: TemplateData, alwaysCap: boolean = false): number {
    if (!alwaysCap && !template.lime.maxBreakActivated) return Infinity

    const rules = template.lime.maxLimeRules
    const cecRules = [
        rules.cec1, rules.cec2, rules.cec3, rules.cec4,
        rules.cec6, rules.cec8, rules.cec10
    ].sort((a, b) => a.cec - b.cec)

    let maxKg = cecRules[0]?.maxKg ?? 8000
    for (const rule of cecRules) {
        if (rule.cec <= cec) {
            maxKg = rule.maxKg
        }
    }
    return maxKg
}

/**
 * Get max Magnesium product application based on CEC, from the Magnesium template's max rules.
 * Mirrors getMaxLimeApplication but reads template.magnesium.
 */
export function getMaxMagnesiumApplication(cec: number, template: TemplateData, alwaysCap: boolean = false): number {
    if (!alwaysCap && !template.magnesium.maxBreakActivated) return Infinity

    const rules = template.magnesium.maxLimeRules
    const cecRules = [
        rules.cec1, rules.cec2, rules.cec3, rules.cec4,
        rules.cec6, rules.cec8, rules.cec10
    ].sort((a, b) => a.cec - b.cec)

    let maxKg = cecRules[0]?.maxKg ?? 8000
    for (const rule of cecRules) {
        if (rule.cec <= cec) {
            maxKg = rule.maxKg
        }
    }
    return maxKg
}

/**
 * FIX 6: Get max gypsum application based on CEC from template rules.
 * Excel: VLOOKUP(BJ19, Parameters!$AA$34:$AB$40, 2, TRUE)
 * Replaces hardcoded 4000.
 *
 * The Excel gypsum year columns (BN/BO/BR/BS/BV/BW) cap unconditionally — there is no
 * max-break toggle for gypsum in the workbook. Callers pass alwaysCap=true to match that.
 * 
 */
export function getMaxGypsumApplication(cec: number, template: TemplateData, alwaysCap: boolean = false): number {
    if (!alwaysCap && !template.gypsum.maxBreakActivated) return Infinity

    const rules = template.gypsum.rules
    const cecRules = [
        rules.cec1, rules.cec2, rules.cec3, rules.cec4,
        rules.cec6, rules.cec8, rules.cec10
    ].sort((a, b) => a.cec - b.cec)

    let maxKg = cecRules[0]?.maxKg ?? 5000
    for (const rule of cecRules) {
        if (rule.cec <= cec) {
            maxKg = rule.maxKg
        }
    }
    return maxKg
}

/**
 * FIX 7: Get max K application based on K% from template break points.
 * Excel: VLOOKUP(BY19, Parameters!$Y$60:$Z$66, 2, TRUE)
 * K% thresholds are fixed (0.02-0.08), max application values come from template.
 */
export function getMaxKApplication(kPercent: number, template: TemplateData): number {
    if (!template.potassium.breakActivated) return Infinity

    const breakPoints = [
        { threshold: K_PERCENT_THRESHOLDS[0]!, max: template.potassium.k1.maxApplicationKgHa },
        { threshold: K_PERCENT_THRESHOLDS[1]!, max: template.potassium.k2.maxApplicationKgHa },
        { threshold: K_PERCENT_THRESHOLDS[2]!, max: template.potassium.k3.maxApplicationKgHa },
        { threshold: K_PERCENT_THRESHOLDS[3]!, max: template.potassium.k4.maxApplicationKgHa },
        { threshold: K_PERCENT_THRESHOLDS[4]!, max: template.potassium.k5.maxApplicationKgHa },
        { threshold: K_PERCENT_THRESHOLDS[5]!, max: template.potassium.k6.maxApplicationKgHa },
        { threshold: K_PERCENT_THRESHOLDS[6]!, max: template.potassium.k7.maxApplicationKgHa },
    ]

    // VLOOKUP with TRUE = find largest threshold <= kPercent
    let maxKg = breakPoints[0]!.max
    for (const bp of breakPoints) {
        if (bp.threshold <= kPercent) {
            maxKg = bp.max
        }
    }
    return maxKg
}

// ============================================================================
// Yearly Application Functions
// ============================================================================

/**
 * Calculate applied year 1 (kg/ha)
 * Excel BF19: Complex formula limiting to max application and min threshold
 */
export function calcAppliedYear1(
    amendmentNeed: number,
    maxApplication: number,
    minRate: number,
    minBreakActivated: boolean,
    phKcl: number,
    phWarningThreshold: number,
    applyBrakes: boolean,
    minEconomicallyFeasibleBreakActivated: boolean = false,
    minEconomicallyFeasibleKgHa: number = 0
): number {
    if (applyBrakes && phKcl >= phWarningThreshold) {
        return 0
    }
    if (minBreakActivated && amendmentNeed < minRate) {
        return 0
    }
    if (minEconomicallyFeasibleBreakActivated && amendmentNeed < minEconomicallyFeasibleKgHa) {
        return 0
    }
    return Math.min(amendmentNeed, maxApplication)
}

/**
 * Calculate applied year 2 (kg/ha)
 * Excel BG19: Remaining after year 1, limited by max application
 */
export function calcAppliedYear2(
    amendmentNeed: number,
    appliedYear1: number,
    maxApplication: number,
    minRate: number
): number {
    if (appliedYear1 <= 0) return 0
    const remaining = amendmentNeed - appliedYear1
    if (remaining < minRate) return 0
    return Math.min(remaining, maxApplication)
}

/**
 * Calculate applied year 3 (kg/ha)
 * Excel BH19: Remaining after year 1+2, limited by max application
 */
export function calcAppliedYear3(
    amendmentNeed: number,
    appliedYear1: number,
    appliedYear2: number,
    maxApplication: number,
    minRate: number
): number {
    if (appliedYear2 <= 0) return 0
    const remaining = amendmentNeed - appliedYear1 - appliedYear2
    if (remaining < minRate) return 0
    return Math.min(remaining, maxApplication)
}

// ============================================================================
// Gypsum Functions
// ============================================================================

/**
 * Check if gypsum is meaningful (Gypsum Rules)
 * Excel BI19: =IF(AND(gypsumNeed>=minApp, Na%>minNa%),"YES","NO")
 */
export function calcGypsumMeaningful(
    gypsumNeed: number,
    naPercent: number,
    minApplicationKgHa: number,
    minNaPercentage: number
): boolean {
    if (gypsumNeed < minApplicationKgHa) return false
    if (naPercent <= minNaPercentage) return false
    return true
}

/**
 * Calculate gypsum need for Na leaching (kg/ha) - PURPOSE 1
 * Excel BL19: =(Na% - finalNaLevel) * CEC * SGCa * depthFactor / gypsumCa%
 */
export function calcGypsumNeedNaLeaching(
    naPercent: number,
    finalNaLeachingLevel: number,
    cec: number,
    depthFactor: number,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    const naDeficit = naPercent - finalNaLeachingLevel
    if (naDeficit <= 0) return 0
    const caNeed = naDeficit * cec * ELEMENT_SPECIFIC_GRAVITY.Ca
    return caNeed * depthFactor / products.gypsum.caPercent
}

/**
 * FIX 5: Calculate gypsum need for Mg leaching (kg/ha) - PURPOSE 2
 * Excel BP19: =((Mg_cmolc - (idealMg% * CEC)) * SGCa / gypsumCa% * depthFactor) + naLeachingGypsum
 */
export function calcGypsumNeedMgLeaching(
    mgCmolc: number,
    mgPercentNorm: number,
    cec: number,
    depthFactor: number,
    naLeachingGypsum: number,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    const idealMgCmolc = mgPercentNorm * cec
    const mgExcess = mgCmolc - idealMgCmolc
    if (mgExcess <= 0) return naLeachingGypsum

    const gypsumForMg = mgExcess * ELEMENT_SPECIFIC_GRAVITY.Ca / products.gypsum.caPercent * depthFactor
    return gypsumForMg + naLeachingGypsum
}

/**
 * FIX 5: Calculate gypsum need for Ca balancing (kg/ha) - PURPOSE 3
 * Excel BT19: Complex formula involving post-lime Ca% prediction.
 * When mainCaSource=Lime: ((targetCa% - postLimeCa%/newCEC) * CEC * SGCa * depthFactor / gypsumCa%)
 * When mainCaSource=Gypsum: (caDiff * depthFactor / gypsumCa%)
 */
export function calcGypsumNeedCaBalancing(
    caDiffMgKg: number,
    caPercentNorm: number,
    caMgKg: number,
    cec: number,
    limeType: LimeType,
    appliedYear1: number,
    appliedYear2: number,
    depthFactor: number,
    mainCaSource: string,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    if (mainCaSource === 'Lime') {
        // Post-lime Ca calculation
        const product = limeType === 'Dolomitic' ? products.dolomitic : products.calcitic
        const totalApplied = appliedYear1 + appliedYear2
        const caFromLime = limeType === 'Dolomitic'
            ? (totalApplied / product.cce) * product.caPercent
            : (totalApplied * product.cce) * product.caPercent
        const newCaMgKg = caMgKg + caFromLime / depthFactor
        const newCaCmolc = newCaMgKg / ELEMENT_SPECIFIC_GRAVITY.Ca

        // Estimate new CEC (simplified - actual CEC changes with amendments)
        const caDeficitInCec = caPercentNorm - (newCaCmolc / cec)
        if (caDeficitInCec <= 0) return 0

        return caDeficitInCec * cec * ELEMENT_SPECIFIC_GRAVITY.Ca * depthFactor / products.gypsum.caPercent
    } else {
        // Main source is gypsum - use Ca deficit directly
        if (caDiffMgKg >= 0) return 0
        return Math.abs(caDiffMgKg) * depthFactor / products.gypsum.caPercent
    }
}

/**
 * FIX 5: Select correct gypsum calculation based on template purpose.
 */
export function calcGypsumNeed(
    naPercent: number,
    mgCmolc: number,
    mgPercentNorm: number,
    caDiffMgKg: number,
    caPercentNorm: number,
    caMgKg: number,
    cec: number,
    limeType: LimeType,
    appliedYear1: number,
    appliedYear2: number,
    depthFactor: number,
    template: TemplateData,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    const purpose = template.gypsum.purpose

    // Na leaching is always calculated as it's used by other purposes too
    const naLeachingGypsum = calcGypsumNeedNaLeaching(
        naPercent, template.gypsum.finalNaLeachingLevel, cec, depthFactor, products
    )

    if (purpose === GypsumPurpose.LeachingNa) {
        return naLeachingGypsum
    }

    if (purpose === GypsumPurpose.LeachingMg) {
        return calcGypsumNeedMgLeaching(mgCmolc, mgPercentNorm, cec, depthFactor, naLeachingGypsum, products)
    }

    if (purpose === GypsumPurpose.CaSource) {
        return calcGypsumNeedCaBalancing(
            caDiffMgKg, caPercentNorm, caMgKg, cec, limeType,
            appliedYear1, appliedYear2, depthFactor,
            template.gypsum.mainCaSource, products
        )
    }

    return naLeachingGypsum // fallback
}

// ============================================================================
// K Fertiliser Functions
// ============================================================================

/**
 * Calculate K need (kg/ha)
 * Excel CA19: =AO19/$AW$6*BX19
 */
export function calcKNeed(
    kDiffMgKg: number,
    depthFactor: number,
    kProductPercent: number = 0.4
): number {
    if (kDiffMgKg >= 0) return 0
    if (kProductPercent === 0) return 0
    const need = Math.abs(kDiffMgKg) / kProductPercent * depthFactor
    return Math.max(0, need)
}

/**
 * Calculate K fertiliser applied year 1 (kg/ha)
 * FIX 7: Uses template K break points for max application
 * Excel CC19: limited by VLOOKUP on K% break points
 */
export function calcKAppliedYear1(
    kNeedKgHa: number,
    maxKgHa: number,
    splitOver2Years: boolean = false
): number {
    if (kNeedKgHa <= 0) return 0
    if (splitOver2Years && kNeedKgHa > maxKgHa) {
        return maxKgHa
    }
    return Math.min(kNeedKgHa, maxKgHa)
}

/**
 * Calculate K fertiliser applied year 2 (kg/ha)
 */
export function calcKAppliedYear2(
    kNeedKgHa: number,
    appliedYear1: number,
    maxKgHa: number,
    splitOver2Years: boolean = false
): number {
    if (!splitOver2Years) return 0
    const remaining = kNeedKgHa - appliedYear1
    if (remaining <= 0) return 0
    return Math.min(remaining, maxKgHa)
}

// ============================================================================
// P Fertiliser Functions
// ============================================================================

/**
 * Calculate P fertiliser need (kg/ha)
 * FIX 8: Uses template P parameters and P fixing fraction.
 * Excel CI19: =(idealP - P) / pProductPercent * depthFactor * (1 + fixingFraction)
 */
export function calcPNeed(
    pBrayI: number,
    idealP: number,
    depthFactor: number,
    pProductPercent: number = 0.22,
    useEstimatedFixingFraction: boolean = false,
    estimatedFixingFractionValue: number = 0
): number {
    const deficit = idealP - pBrayI
    if (deficit <= 0) return 0
    if (pProductPercent === 0) return 0
    const basNeed = deficit / pProductPercent * depthFactor
    // Apply fixing fraction if enabled
    if (useEstimatedFixingFraction) {
        return basNeed * (1 + estimatedFixingFractionValue)
    }
    return basNeed
}

/**
 * Calculate P fertiliser applied year 1 (kg/ha)
 * FIX 8: Uses template min/max application and monitoring cap.
 */
export function calcPAppliedYear1(
    pNeedKgHa: number,
    minKgHa: number = 0,
    maxKgHa: number = 300,
    monitoringEnabled: boolean = true
): number {
    // Excel CK19 floors a valid row at the P minimum (M26) even when the computed need is 0
    // (adequate-P soils), not just when 0 < need < min. 
    if (pNeedKgHa < minKgHa) return minKgHa
    if (monitoringEnabled) {
        return Math.min(pNeedKgHa, maxKgHa)
    }
    return pNeedKgHa
}

/**
 * Calculate P fertiliser applied year 2 (kg/ha)
 */
export function calcPAppliedYear2(
    pNeedKgHa: number,
    appliedYear1: number,
    maxKgHa: number = 300,
    splitOver2Years: boolean = false
): number {
    if (!splitOver2Years) return 0
    const remaining = pNeedKgHa - appliedYear1
    if (remaining <= 0) return 0
    return Math.min(remaining, maxKgHa)
}

// ============================================================================
// Elemental Sulphur Functions
// ============================================================================

/**
 * Get pH operator for elemental sulphur lookup
 * Excel CM19: =VLOOKUP(V19,Parameters!$M$12:$N$18,2,TRUE)
 */
export function lookupPhOperator(cec: number): number {
    if (cec < 6) return 1
    if (cec < 9) return 2
    if (cec < 12) return 3
    if (cec < 18) return 4
    if (cec < 24) return 5
    if (cec < 30) return 6
    return 7
}

/**
 * Calculate elemental sulphur need (kg/ha)
 * Only applies when pH >= 7
 * Excel CN19: uses current pH to select column, Parameters!P10 ("decrease pH to") selects the rate table.
 * Excel formula in sulphur table: =IF($P$10=6.5, AU*depth, AY*depth)
 * Base rates × depth (cm) = kg/ha. Two rate sets: target 5.5 and target 6.5.
 */
export function calcElementalSulphurNeed(
    phKcl: number,
    phOperator: number,
    targetPh: SulphurTargetPh = SulphurTargetPh.Ph55,
    depthCm: number = 30
): number {
    if (phKcl < 7) return 0

    const rates = ELEMENTAL_SULPHUR_BASE_RATES[targetPh]
    const entry = rates.find(e => e.operator === phOperator)
    if (!entry) return 0

    // Excel CN19 uses midpoint boundaries: 7.25, 7.75, 8.25
    if (phKcl >= 8.25) return entry.toPh85 * depthCm
    if (phKcl >= 7.75) return entry.toPh80 * depthCm
    if (phKcl >= 7.25) return entry.toPh75 * depthCm
    if (phKcl >= 7.0) return entry.toPh70 * depthCm
    return 0
}

// ============================================================================
// Nutrient Input Functions (from amendments)
// ============================================================================

/**
 * FIX 14: Calculate Ca input from lime (mg/kg added)
 * Excel CW19:
 *   Dolomitic: (applied / CCE) * Ca%
 *   Calcitic:  (applied * CCE) * Ca%
 */
export function calcCaInputFromLime(
    appliedYear1KgHa: number,
    limeType: LimeType,
    depthFactor: number,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    const product = limeType === 'Dolomitic' ? products.dolomitic : products.calcitic
    let caCorrected: number
    if (limeType === 'Dolomitic') {
        caCorrected = (appliedYear1KgHa / product.cce) * product.caPercent
    } else {
        caCorrected = (appliedYear1KgHa * product.cce) * product.caPercent
    }
    return caCorrected / depthFactor
}

/**
 * FIX 14: Calculate Mg input from lime (mg/kg added)
 * Excel CX19: (applied * Mg%) / depthFactor
 * Note: Excel doesn't divide/multiply by CCE for Mg, just uses straight product.
 */
export function calcMgInputFromLime(
    appliedYear1KgHa: number,
    limeType: LimeType,
    depthFactor: number,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    const product = limeType === 'Dolomitic' ? products.dolomitic : products.calcitic
    // Excel CX19: (BF19*$AX$3)/AQ19 for dolomitic, (BF19*$AX$2)/AQ19 for calcitic
    const mgCorrected = appliedYear1KgHa * product.mgPercent
    return mgCorrected / depthFactor
}

/**
 * Mg added to the soil (mg/kg) by the standalone Magnesium product, for the Year 1/2 projections.
 * Mirrors calcMgInputFromLime but reads the Magnesium product. Pass the UN-surface-reduced applied
 * amount (magnesiumYear1/2KgHa, not the finalMagnesium* values), matching the other input formulas.
 */
export function calcMgInputFromMagnesium(
    magnesiumAppliedKgHa: number,
    depthFactor: number,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    return (magnesiumAppliedKgHa * products.magnesium.mgPercent) / depthFactor
}

/**
 * FIX 9: Calculate Ca input from gypsum (mg/kg added)
 * Excel CW19 includes: + gypsumApplied * gypsumCa% / depthFactor
 */
export function calcCaInputFromGypsum(
    gypsumYear1KgHa: number,
    depthFactor: number,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    return (gypsumYear1KgHa * products.gypsum.caPercent) / depthFactor
}

/**
 * Calculate K input from fertiliser (mg/kg added)
 */
export function calcKInputFromFert(
    kAppliedYear1KgHa: number,
    depthFactor: number,
    kProductPercent: number = 0.4
): number {
    return (kAppliedYear1KgHa * kProductPercent) / depthFactor
}

/**
 * Calculate P input from fertiliser (mg/kg added)
 */
export function calcPInputFromFert(
    pAppliedYear1KgHa: number,
    depthFactor: number,
    pProductPercent: number = 0.22
): number {
    return (pAppliedYear1KgHa * pProductPercent) / depthFactor
}

// ============================================================================
// FIX 10: Na Prediction After Gypsum
// ============================================================================

/**
 * Calculate Na reduction from gypsum application.
 * Excel DI19: Na - (gypsumApplied * gypsumCa% / depthFactor / SGCa * SGNa)
 * Gypsum displaces Na via Ca exchange.
 */
export function calcNewNaAfterGypsum(
    naMgKg: number,
    gypsumYear1KgHa: number,
    depthFactor: number,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    if (gypsumYear1KgHa <= 0) return naMgKg
    const naDisplaced = gypsumYear1KgHa * products.gypsum.caPercent / depthFactor / ELEMENT_SPECIFIC_GRAVITY.Ca * ELEMENT_SPECIFIC_GRAVITY.Na
    return Math.max(0, naMgKg - naDisplaced)
}

// ============================================================================
// FIX 11: Mg Prediction After Gypsum Leaching
// ============================================================================

/**
 * Calculate Mg reduction from gypsum leaching effect.
 * Excel CX19 includes: - IF(leachMgInPrediction, (gypsumForMg - gypsumForNa) * gypsumCa% / SGCa / depthFactor * SGMg, 0)
 * Only applies when purpose is MgLeaching or NaLeaching (when excess gypsum leaches Mg).
 */
export function calcMgLeachingFromGypsum(
    gypsumYear1KgHa: number,
    naLeachingGypsum: number,
    depthFactor: number,
    leachMgInPrediction: boolean,
    gypsumPurpose: string,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    if (!leachMgInPrediction || gypsumYear1KgHa <= 0) return 0

    // Only the portion above Na leaching gypsum displaces Mg
    let excessGypsum = 0
    if (gypsumPurpose === GypsumPurpose.LeachingMg) {
        excessGypsum = gypsumYear1KgHa - naLeachingGypsum
    } else if (gypsumPurpose === GypsumPurpose.LeachingNa) {
        excessGypsum = Math.max(0, gypsumYear1KgHa - naLeachingGypsum)
    } else if (gypsumPurpose === GypsumPurpose.CaSource) {
        excessGypsum = Math.max(0, gypsumYear1KgHa - naLeachingGypsum)
    }

    if (excessGypsum <= 0) return 0
    return excessGypsum * products.gypsum.caPercent / ELEMENT_SPECIFIC_GRAVITY.Ca / depthFactor * ELEMENT_SPECIFIC_GRAVITY.Mg
}

/**
 * Calculate Year-2 Mg reduction from gypsum leaching.
 * Excel EE19: the Year-2 excess carries the Year-1 shortfall forward —
 *   excess = IF(gypsumY1 > naLeachBaseline, gypsumY2, gypsumY1 - naLeachBaseline + gypsumY2)
 * so when Year-1 gypsum did NOT clear the Na baseline, only the portion of Year-2 above the
 * remaining baseline leaches Mg. (The previous code passed a 0 baseline, always leaching the
 * full Year-2 amount.) The Leaching-Na branch clamps the term at 0; Mg/Ca-source do not.
 * 
 */
export function calcMgLeachingFromGypsumYear2(
    gypsumYear1KgHa: number,
    gypsumYear2KgHa: number,
    naLeachingGypsum: number,
    depthFactor: number,
    leachMgInPrediction: boolean,
    gypsumPurpose: string,
    products: ProductValues = DEFAULT_PRODUCT_VALUES
): number {
    if (!leachMgInPrediction) return 0

    const excessGypsum = gypsumYear1KgHa > naLeachingGypsum
        ? gypsumYear2KgHa
        : (gypsumYear1KgHa - naLeachingGypsum + gypsumYear2KgHa)

    const term = excessGypsum * products.gypsum.caPercent / ELEMENT_SPECIFIC_GRAVITY.Ca / depthFactor * ELEMENT_SPECIFIC_GRAVITY.Mg

    // Excel clamps only the Leaching-Na branch at 0
    if (gypsumPurpose === GypsumPurpose.LeachingNa) return Math.max(0, term)
    return term
}

// ============================================================================
// Prediction Functions
// ============================================================================

/**
 * Calculate new nutrient level after amendments (mg/kg)
 */
export function calcNewNutrientLevel(
    currentMgKg: number,
    inputMgKg: number
): number {
    return currentMgKg + inputMgKg
}

/**
 * Calculate new saturation percentage after amendments
 * Formula: (newMgKg / specificGravity) / newCec
 */
export function calcNewSaturationPercent(
    newMgKg: number,
    element: Element,
    newCec: number
): number {
    if (newCec === 0) return 0
    return (newMgKg / ELEMENT_SPECIFIC_GRAVITY[element]) / newCec
}

/**
 * Calculate new CEC (T-value) after amendments
 * Formula: Sum of all cations in cmolc/kg
 */
export function calcNewCec(
    newCaMgKg: number,
    newMgMgKg: number,
    newKMgKg: number,
    newNaMgKg: number
): number {
    return newCaMgKg / ELEMENT_SPECIFIC_GRAVITY.Ca +
           newMgMgKg / ELEMENT_SPECIFIC_GRAVITY.Mg +
           newKMgKg / ELEMENT_SPECIFIC_GRAVITY.K +
           newNaMgKg / ELEMENT_SPECIFIC_GRAVITY.Na
}

/**
 * Calculate new Ca:Mg ratio after amendments
 */
export function calcNewCaMgRatio(
    newCaPercent: number,
    newMgPercent: number
): number {
    if (newMgPercent === 0) return 0
    return newCaPercent / newMgPercent
}

// ============================================================================
// Main Calculation Function
// ============================================================================

/**
 * Calculate all derived values for a lab input row.
 * Incorporates all 16 fixes from the Excel alignment audit.
 */
export function calculateLabInfoRow(row: LabInputRow, template: TemplateData, options?: CalcOptions): LabInfoRow {
    const products = options?.products ?? DEFAULT_PRODUCT_VALUES
    // A sample carries either pH (H2O) or pH (KCl); run the calc on whichever is present.
    const ph = resolvePh(row)

    // NULL feature: which products are actually selected. Absent → all selected (current behaviour).
    const sel = options?.productSelected
    const calciticSelected = sel ? sel.calcitic : true
    const dolomiticSelected = sel ? sel.dolomitic : true
    const magnesiumSelected = sel ? sel.magnesium : true
    const gypsumSelected = sel ? sel.gypsum : true
    const potassiumSelected = sel ? sel.potassium : true
    const phosphateSelected = sel ? sel.phosphate : true

    // The selected P analysis determines both which imported P value is used and which template
    // ideal-P parameter is read. Absent → Bray I (historical single-analysis behaviour).
    const pAnalysisMethod = options?.pAnalysisMethod ?? PAnalysisMethod.BrayI
    const selectedP = selectPValue(row, pAnalysisMethod)
    const idealP = selectIdealP(template.phosphorus, pAnalysisMethod)

    // FIX 15: Separate lime/gypsum depth from P/K depth.
    // Lime/gypsum/Mg and P/K depths are configured on the program master tab (two groups).
    // Elemental sulphur keeps its own depth on the template. Default to 30cm when no master value.
    const limeGypsumDepth = options?.limeGypsumDepthCm ?? 30
    const pkDepth = options?.pkDepthCm ?? limeGypsumDepth

    const kPercent = row.kPercent / 100
    const naPercent = row.naPercent / 100
    const caPercent = row.caPercent / 100
    const mgPercent = row.mgPercent / 100

    // Lookup operators
    const cecOperator = lookupCecOperator(row.cec)
    const densityOperator = lookupDensityOperator(row.density)

    // FIX 12: Get base saturation norms by CEC or Density depending on template setting
    let norms: { ca: number; mg: number; k: number; caMgRatio: number }
    if (template.baseSaturation.normBasis === BalancingNormBasis.Density) {
        norms = getBaseSaturationNormsByDensity(row.density, template)
    } else {
        norms = getBaseSaturationNormsByCec(row.cec, template)
    }

    // Calculate nutrient levels at norms (mg/kg)
    const caNormMgKg = calcNutrientLevelAtNorm(norms.ca, row.cec, 'Ca')
    const mgNormMgKg = calcNutrientLevelAtNorm(norms.mg, row.cec, 'Mg')
    const kNormMgKg = calcNutrientLevelAtNorm(norms.k, row.cec, 'K')

    // Calculate nutrient differences
    const caDiffMgKg = row.ca - caNormMgKg
    const mgDiffMgKg = row.mg - mgNormMgKg
    const kDiffMgKg = row.k - kNormMgKg

    // FIX 15: Lime/gypsum depth factor and P/K depth factor
    const depthFactor = lookupDepthFactor(densityOperator, limeGypsumDepth)
    const pkDepthFactor = lookupDepthFactor(densityOperator, pkDepth)

    // FIX 4: Determine lime type with full Excel priority logic
    const calculatedLimeType = determineLimeType(
        caDiffMgKg, mgDiffMgKg, row.cec, row.mg, row.ca,
        mgPercent, norms.mg, template
    )
    const requestedLimeType: LimeType = (row.limeTypeOverride === 'Calcitic' || row.limeTypeOverride === 'Dolomitic')
        ? row.limeTypeOverride
        : calculatedLimeType

    // NULL feature: clamp the lime type to the products actually selected — you can't apply a
    // product you didn't pick. Both lime products NULL → the Lime section is suppressed.
    const limeAvailable: LimeType[] = []
    if (calciticSelected) limeAvailable.push('Calcitic')
    if (dolomiticSelected) limeAvailable.push('Dolomitic')
    const limeSuppressed = limeAvailable.length === 0
    const limeType: LimeType = limeAvailable.includes(requestedLimeType)
        ? requestedLimeType
        : (limeAvailable[0] ?? requestedLimeType)

    // Calculate lime rule (is lime needed?)
    const limeRule = calcLimeRule(caPercent, norms.ca, mgPercent, norms.mg, row.mg, row.ca, row.exchH, ph, template)

    // Calculate Ca/Mg amendment supply (FIX 3: uses actual products)
    const caSupplyKgHa = calcCaAmendmentSupply(caDiffMgKg, depthFactor, limeType, products)
    const mgSupplyKgHa = calcMgAmendmentSupply(mgDiffMgKg, depthFactor, limeType, products)

    // Calculate ideal supplies (FIX 3: uses actual products)
    const mgIdealSupply = calcMgIdealSupply(row.mg, template.lime.idealMgMgKg, depthFactor, template.lime.reachIdealMg, products)
    const caIdealSupply = calcCaIdealSupply(row.ca, template.lime.idealCaMgKg, depthFactor, template.lime.reachIdealCa, limeType, products)

    // FIX 1: pH-based lime calculation
    const phBasedLime = calcPhBasedLimeNeed(
        ph, template.lime.desiredPh, row.cec, limeType, limeGypsumDepth, template, products
    )

    // FIX 2: Exchangeable acid neutralization
    const exchAcidLime = calcExchAcidLimeNeed(row.exchH, limeType, depthFactor, template.lime.neutraliseAcid, products)

    // Total amendment need: MAX of supply (conditional on lime type), ideal supplies, pH-based
    // lime, and exch acid.
    const amendmentNeedKgHa = limeSuppressed ? 0 : calcAmendmentNeed(
        caSupplyKgHa, mgSupplyKgHa, mgIdealSupply, caIdealSupply,
        phBasedLime, exchAcidLime, limeType
    )
    // Suppression (no lime product) wins over a manual override — nothing to apply.
    const effectiveAmendmentNeed = limeSuppressed ? 0 : (row.limeNeedOverride ?? amendmentNeedKgHa)

    // Max application and yearly calculations
    const maxApplication = getMaxLimeApplication(row.cec, template)
    const appliedYear1KgHa = calcAppliedYear1(
        effectiveAmendmentNeed,
        maxApplication,
        template.lime.minRateKgHa,
        template.lime.minBreakActivated,
        ph,
        template.lime.phWarningThreshold,
        template.lime.applyBrakes,
        template.lime.minEconomicallyFeasibleBreakActivated,
        template.lime.minEconomicallyFeasibleKgHa
    )
    // Excel BG19/BH19 use Parameters!$Y$12 (minEconomicallyFeasibleKgHa) unconditionally for Y2/Y3 threshold.
    // Excel BG19/BH19 also cap unconditionally (no max-break toggle for years 2/3) — B5.
    const limeMaxAlwaysCap = getMaxLimeApplication(row.cec, template, true)
    const appliedYear2KgHa = calcAppliedYear2(effectiveAmendmentNeed, appliedYear1KgHa, limeMaxAlwaysCap, template.lime.minEconomicallyFeasibleKgHa)
    const appliedYear3KgHa = calcAppliedYear3(effectiveAmendmentNeed, appliedYear1KgHa, appliedYear2KgHa, limeMaxAlwaysCap, template.lime.minEconomicallyFeasibleKgHa)

    // Magnesium product need: the dolomitic Mg formula using the dedicated Mg product, with NO
    // pH-based need (soil-buffering removed per spec). Driven by the Mg deficit vs base-saturation
    // norm (mgDiffMgKg) plus optional ideal-Mg, capped by the Magnesium template's max rules and
    // split into Y1/Y2/Y3 like dolomitic lime. Shares the lime/gypsum depth factor.
    // Magnesium has no pH-based *need*, but its Year 1 application still honours the configured
    // high-pH brake (apply_brakes + ph_warning_threshold) — a Mg carbonate still raises pH.
    const mgProductSupplyKgHa = calcMagnesiumProductSupply(mgDiffMgKg, depthFactor, products)
    const magnesiumIdealSupply = calcMagnesiumProductIdealSupply(
        row.mg, template.magnesium.idealMgMgKg, depthFactor, template.magnesium.reachIdealMg, products
    )
    // NULL feature: no Mg product selected → Magnesium strategy skipped entirely.
    // Magnesium does NOT drive pH (no pH-based need). Need = MAX(Mg deficit, ideal-Mg).
    const magnesiumNeedKgHa = magnesiumSelected ? Math.max(mgProductSupplyKgHa, magnesiumIdealSupply, 0) : 0
    const magnesiumRule = magnesiumNeedKgHa > 0
    const effectiveMagnesiumNeed = magnesiumSelected ? (row.magnesiumNeedOverride ?? magnesiumNeedKgHa) : 0

    const magnesiumMax = getMaxMagnesiumApplication(row.cec, template)
    const magnesiumYear1KgHa = calcAppliedYear1(
        effectiveMagnesiumNeed,
        magnesiumMax,
        template.magnesium.minRateKgHa,
        template.magnesium.minBreakActivated,
        ph,
        // pH-high brake: don't apply Mg if soil pH is already at/above the warning threshold.
        template.magnesium.phWarningThreshold,
        template.magnesium.applyBrakes,
        template.magnesium.minEconomicallyFeasibleBreakActivated,
        template.magnesium.minEconomicallyFeasibleKgHa
    )
    const magnesiumMaxAlwaysCap = getMaxMagnesiumApplication(row.cec, template, true)
    const magnesiumYear2KgHa = calcAppliedYear2(
        effectiveMagnesiumNeed, magnesiumYear1KgHa, magnesiumMaxAlwaysCap, template.magnesium.minEconomicallyFeasibleKgHa
    )
    // Magnesium splits over 3 years like the dolomitic lime it mirrors.
    const magnesiumYear3KgHa = calcAppliedYear3(
        effectiveMagnesiumNeed, magnesiumYear1KgHa, magnesiumYear2KgHa, magnesiumMaxAlwaysCap, template.magnesium.minEconomicallyFeasibleKgHa
    )

    // FIX 5+6: Gypsum calculations with full purpose support and template max rules.
    // Gypsum shares the master lime/gypsum depth (grouped with lime and Mg).
    const gypsumDepthFactor = lookupDepthFactor(densityOperator, limeGypsumDepth)
    const mgCmolc = mgKgToCmolc(row.mg, 'Mg')

    const calculatedGypsumNeed = calcGypsumNeed(
        naPercent, mgCmolc, norms.mg, caDiffMgKg, norms.ca, row.ca, row.cec,
        limeType, appliedYear1KgHa, appliedYear2KgHa, gypsumDepthFactor, template, products
    )
    const gypsumNeedKgHa = gypsumSelected ? calculatedGypsumNeed : 0
    const effectiveGypsumNeed = gypsumSelected ? (row.gypsumNeedOverride ?? gypsumNeedKgHa) : 0
    // B1: the Excel gates each gypsum purpose differently.
    //  - Leaching Na (BI/BN): need >= minApp AND Na% > minNa
    //  - Leaching Mg (BR):    gated on the leach-Mg flag (W42) and need > minApp; no Na% test
    //  - Ca Source (BV):      no meaningfulness gate — apply whatever is needed (still capped)
    let gypsumMeaningful: boolean
    if (template.gypsum.purpose === GypsumPurpose.LeachingMg) {
        gypsumMeaningful = template.gypsum.leachMgInPrediction && effectiveGypsumNeed > template.gypsum.minApplicationKgHa
    } else if (template.gypsum.purpose === GypsumPurpose.CaSource) {
        gypsumMeaningful = effectiveGypsumNeed > 0
    } else {
        gypsumMeaningful = calcGypsumMeaningful(effectiveGypsumNeed, naPercent, template.gypsum.minApplicationKgHa, template.gypsum.minNaPercentage)
    }

    // FIX 6: Gypsum yearly application using template max rules.
    // Gypsum has no max-break toggle in the Excel — it always caps (B5).
    const gypsumMaxPerYear = getMaxGypsumApplication(row.cec, template, true)
    const gypsumYear1KgHa = gypsumMeaningful ? Math.min(effectiveGypsumNeed, gypsumMaxPerYear) : 0
    const gypsumYear2KgHa = gypsumMeaningful ? Math.min(Math.max(0, effectiveGypsumNeed - gypsumYear1KgHa), gypsumMaxPerYear) : 0

    // FIX 7+15: K fertiliser calculations with template break points and separate P/K depth
    const kProductPercent = products.potassium.kPercent
    let kNeedKgHa: number
    if (!potassiumSelected) {
        // NULL feature: no potassium product selected → K strategy skipped.
        kNeedKgHa = 0
    } else if (template.potassium.recommendationBasis === KRecommendationBasis.IdealKMgKg) {
        // Ideal K method: (idealK - actualK) / kProduct% * depthFactor
        const kDeficit = template.potassium.idealMgKg - row.k
        kNeedKgHa = kDeficit > 0 ? kDeficit / kProductPercent * pkDepthFactor : 0
    } else {
        // Base saturation method (default)
        kNeedKgHa = calcKNeed(kDiffMgKg, pkDepthFactor, kProductPercent)
    }
    const effectiveKNeed = potassiumSelected ? (row.kNeedOverride ?? kNeedKgHa) : 0
    const kMaxApplication = getMaxKApplication(kPercent, template)
    const kFertYear1KgHa = calcKAppliedYear1(effectiveKNeed, kMaxApplication, template.potassium.splitOver2Years)
    const kFertYear2KgHa = calcKAppliedYear2(effectiveKNeed, kFertYear1KgHa, kMaxApplication, template.potassium.splitOver2Years)

    // FIX 8+15: P fertiliser calculations with template parameters and separate P/K depth
    const pProductPercent = products.phosphate.pPercent
    // NULL feature: no phosphate product selected → P strategy skipped.
    const pNeedKgHa = phosphateSelected ? calcPNeed(
        selectedP, idealP, pkDepthFactor, pProductPercent,
        template.phosphorus.useEstimatedFixingFraction,
        template.phosphorus.estimatedFixingFractionValue
    ) : 0
    const effectivePNeed = phosphateSelected ? (row.pNeedOverride ?? pNeedKgHa) : 0
    const pFertYear1KgHa = calcPAppliedYear1(effectivePNeed, template.phosphorus.minApplicationKgHa, template.phosphorus.maxApplicationKgHa, template.phosphorus.monitoringEnabled)
    const pFertYear2KgHa = calcPAppliedYear2(effectivePNeed, pFertYear1KgHa, template.phosphorus.maxApplicationKgHa, template.phosphorus.splitOver2Years)

    // Elemental Sulphur calculations
    const phOperator = lookupPhOperator(row.cec)
    // Elemental sulphur keeps its own depth on the template (not part of the master depth groups).
    const calculatedElementalSulphur = calcElementalSulphurNeed(
        ph, phOperator, template.elementalSulphur.targetPh, template.elementalSulphur.depthCm
    )
    const elementalSulphurKgHa = row.elementalSulphurOverride ?? calculatedElementalSulphur

    // Apply surface application percentages (if activated)
    const surfaceApp = template.surfaceApplication
    const limeGypsumFactor = surfaceApp?.activated ? (surfaceApp.limeGypsumPercentage ?? 1) : 1
    const pkFactor = surfaceApp?.activated ? (surfaceApp.pkPercentage ?? 1) : 1

    // Coarse fragment adjustment - reduces lime based on % of coarse fragments in soil
    const coarseFragmentFactor = surfaceApp?.adjustCoarseFragment
        ? (1 - (surfaceApp.reduceLimePercentage ?? 0))
        : 1

    // Apply surface reduction to final applied values
    // TODO: Coarse fragment (F25) is a dead cell in the Excel — never wired into any formula.
    // Awaiting client approval to re-enable. When approved, apply coarseFragmentFactor to LIME ONLY:
    //   finalAppliedYear1KgHa = appliedYear1KgHa * limeGypsumFactor * coarseFragmentFactor
    //   finalAppliedYear2KgHa = appliedYear2KgHa * limeGypsumFactor * coarseFragmentFactor
    //   finalAppliedYear3KgHa = appliedYear3KgHa * limeGypsumFactor * coarseFragmentFactor
    const finalAppliedYear1KgHa = appliedYear1KgHa * limeGypsumFactor
    const finalAppliedYear2KgHa = appliedYear2KgHa * limeGypsumFactor
    const finalAppliedYear3KgHa = appliedYear3KgHa * limeGypsumFactor
    const finalGypsumYear1KgHa = gypsumYear1KgHa * limeGypsumFactor
    const finalGypsumYear2KgHa = gypsumYear2KgHa * limeGypsumFactor
    // Magnesium shares the lime/gypsum surface-application factor.
    const finalMagnesiumYear1KgHa = magnesiumYear1KgHa * limeGypsumFactor
    const finalMagnesiumYear2KgHa = magnesiumYear2KgHa * limeGypsumFactor
    const finalMagnesiumYear3KgHa = magnesiumYear3KgHa * limeGypsumFactor
    const finalKFertYear1KgHa = kFertYear1KgHa * pkFactor
    const finalKFertYear2KgHa = kFertYear2KgHa * pkFactor
    const finalPFertYear1KgHa = pFertYear1KgHa * pkFactor
    const finalPFertYear2KgHa = pFertYear2KgHa * pkFactor
    const finalElementalSulphurKgHa = elementalSulphurKgHa * limeGypsumFactor

    // ==========================================================================
    // Year 1 Predictions
    // ==========================================================================
    //
    // NOTE: The predicted new soil levels are driven by the UN-surface-reduced
    // amounts (appliedYear1KgHa / magnesiumYear1KgHa / gypsumYear1KgHa / kFertYear1KgHa / pFertYear1KgHa),
    // NOT the finalXxx values. This mirrors the Excel, where the projection columns
    // (CW/CX/CY/CZ/DI) reference the "Applied Year 1" recommendation columns
    // (BF/BN/CG/CK) which do NOT include the 0.7 surface-application factor. That
    // factor (Parameters!$F$21/$F$22) is confined to the Application-Data schedule.
    // Do NOT swap these back to finalXxx — that under-projects by the surface factor.

    // FIX 9+14: Ca input from lime AND gypsum
    const caInputFromLime = calcCaInputFromLime(appliedYear1KgHa, limeType, depthFactor, products)
    const caInputFromGypsum = calcCaInputFromGypsum(gypsumYear1KgHa, depthFactor, products)
    const caInputMgKg = caInputFromLime + caInputFromGypsum

    // FIX 11: Mg input from lime minus gypsum Mg leaching
    const mgInputFromLime = calcMgInputFromLime(appliedYear1KgHa, limeType, depthFactor, products)
    const naLeachingGypsumForMgCalc = calcGypsumNeedNaLeaching(
        naPercent, template.gypsum.finalNaLeachingLevel, row.cec, gypsumDepthFactor, products
    )
    const mgLeaching = calcMgLeachingFromGypsum(
        gypsumYear1KgHa, naLeachingGypsumForMgCalc, depthFactor,
        template.gypsum.leachMgInPrediction, template.gypsum.purpose, products
    )
    // Include Mg supplied by the standalone Magnesium product (un-surface-reduced Y1 amount).
    const mgInputFromMagnesium = calcMgInputFromMagnesium(magnesiumYear1KgHa, depthFactor, products)
    const mgInputMgKg = mgInputFromLime + mgInputFromMagnesium - mgLeaching

    // K and P inputs (FIX 15: use P/K depth factor)
    const kInputMgKg = calcKInputFromFert(kFertYear1KgHa, pkDepthFactor, kProductPercent)
    const pInputMgKg = calcPInputFromFert(pFertYear1KgHa, pkDepthFactor, pProductPercent)

    // New calculated nutrient levels (mg/kg) - Year 1
    const newCaMgKg = calcNewNutrientLevel(row.ca, caInputMgKg)
    const newMgMgKg = calcNewNutrientLevel(row.mg, mgInputMgKg)
    const newKMgKg = calcNewNutrientLevel(row.k, kInputMgKg)
    const newPMgKg = calcNewNutrientLevel(selectedP, pInputMgKg)
    // FIX 10: Na prediction after gypsum (un-factored need — Excel DI19 uses BN19)
    const newNaMgKg = calcNewNaAfterGypsum(row.na, gypsumYear1KgHa, depthFactor, products)

    // New CEC and ratios
    const newCec = calcNewCec(newCaMgKg, newMgMgKg, newKMgKg, newNaMgKg)
    const newCaPercent = calcNewSaturationPercent(newCaMgKg, 'Ca', newCec)
    const newMgPercent = calcNewSaturationPercent(newMgMgKg, 'Mg', newCec)
    const newKPercent = calcNewSaturationPercent(newKMgKg, 'K', newCec)
    const newNaPercent = calcNewSaturationPercent(newNaMgKg, 'Na', newCec)
    const newCaMgRatio = calcNewCaMgRatio(newCaPercent, newMgPercent)

    // ==========================================================================
    // FIX 13: Year 2 Predictions
    // ==========================================================================

    // Year 2 predictions also use the un-factored applied amounts (Excel ED/EE/EF/EG/EP
    // reference BG/BO/CH/CD/CL — likewise without the 0.7 surface factor).

    // Year 2 Ca input from lime
    const caInputFromLimeY2 = calcCaInputFromLime(appliedYear2KgHa, limeType, depthFactor, products)
    const caInputFromGypsumY2 = calcCaInputFromGypsum(gypsumYear2KgHa, depthFactor, products)
    const caInputY2 = caInputFromLimeY2 + caInputFromGypsumY2

    // Year 2 Mg input from lime
    const mgInputFromLimeY2 = calcMgInputFromLime(appliedYear2KgHa, limeType, depthFactor, products)
    // B3: carry the Year-1 Na-leach shortfall forward (Excel EE19) instead of assuming a 0 baseline.
    const mgLeachingY2 = calcMgLeachingFromGypsumYear2(
        gypsumYear1KgHa, gypsumYear2KgHa, naLeachingGypsumForMgCalc, depthFactor,
        template.gypsum.leachMgInPrediction, template.gypsum.purpose, products
    )
    // Include Mg supplied by the standalone Magnesium product (un-surface-reduced Y2 amount).
    const mgInputFromMagnesiumY2 = calcMgInputFromMagnesium(magnesiumYear2KgHa, depthFactor, products)
    const mgInputY2 = mgInputFromLimeY2 + mgInputFromMagnesiumY2 - mgLeachingY2

    // Year 2 K and P inputs
    const kInputY2 = calcKInputFromFert(kFertYear2KgHa, pkDepthFactor, kProductPercent)
    const pInputY2 = calcPInputFromFert(pFertYear2KgHa, pkDepthFactor, pProductPercent)

    // Year 2 nutrient levels (applied to Year 1 predictions)
    const newCaMgKgY2 = newCaMgKg + caInputY2
    const newMgMgKgY2 = newMgMgKg + mgInputY2
    const newKMgKgY2 = newKMgKg + kInputY2
    const newPMgKgY2 = newPMgKg + pInputY2
    const newNaMgKgY2 = calcNewNaAfterGypsum(newNaMgKg, gypsumYear2KgHa, depthFactor, products)

    const newCecY2 = calcNewCec(newCaMgKgY2, newMgMgKgY2, newKMgKgY2, newNaMgKgY2)
    const newCaPercentY2 = calcNewSaturationPercent(newCaMgKgY2, 'Ca', newCecY2)
    const newMgPercentY2 = calcNewSaturationPercent(newMgMgKgY2, 'Mg', newCecY2)
    const newKPercentY2 = calcNewSaturationPercent(newKMgKgY2, 'K', newCecY2)
    const newNaPercentY2 = calcNewSaturationPercent(newNaMgKgY2, 'Na', newCecY2)
    const newCaMgRatioY2 = calcNewCaMgRatio(newCaPercentY2, newMgPercentY2)

    return {
        referenceNr: row.referenceNr,
        phH2O: row.phH2O,
        phKcl: row.phKcl,
        pBrayI: row.pBrayI,
        pBrayII: row.pBrayII,
        pOlsen: row.pOlsen,
        pMehlichIII: row.pMehlichIII,
        kMgKg: row.k,
        naMgKg: row.na,
        caMgKg: row.ca,
        mgMgKg: row.mg,
        fe: row.fe,
        mn: row.mn,
        zn: row.zn,
        cu: row.cu,
        exchH: row.exchH,
        sulfur: row.sulfur,
        density: row.density,
        kCmolc: mgKgToCmolc(row.k, 'K'),
        naCmolc: mgKgToCmolc(row.na, 'Na'),
        caCmolc: mgKgToCmolc(row.ca, 'Ca'),
        mgCmolc: mgKgToCmolc(row.mg, 'Mg'),
        pKgHa: mgKgToKgHa(selectedP, row.density, limeGypsumDepth),
        kKgHa: mgKgToKgHa(row.k, row.density, limeGypsumDepth),
        naKgHa: mgKgToKgHa(row.na, row.density, limeGypsumDepth),
        caKgHa: mgKgToKgHa(row.ca, row.density, limeGypsumDepth),
        mgKgHa: mgKgToKgHa(row.mg, row.density, limeGypsumDepth),
        sValue: row.sValue,
        cec: row.cec,
        caMgRatio: ratio(caPercent, mgPercent),
        naKRatio: ratio(naPercent, kPercent),
        caMgKRatio: ratio(caPercent + mgPercent, kPercent),
        kPercent,
        naPercent,
        caPercent,
        mgPercent,
        // Operators
        cecOperator,
        densityOperator,
        // % Base saturation norms
        caPercentNorm: norms.ca,
        mgPercentNorm: norms.mg,
        caMgRatioNorm: norms.caMgRatio,
        kPercentNorm: norms.k,
        // Nutrient level at norms (mg/kg)
        caNormMgKg,
        mgNormMgKg,
        kNormMgKg,
        // Nutrient difference (actual - norm)
        caDiffMgKg,
        mgDiffMgKg,
        kDiffMgKg,
        // Lime calculations
        limeRule,
        depthFactor,
        limeType,
        caSupplyKgHa,
        mgSupplyKgHa,
        amendmentNeedKgHa,
        appliedYear1KgHa: finalAppliedYear1KgHa,
        appliedYear2KgHa: finalAppliedYear2KgHa,
        appliedYear3KgHa: finalAppliedYear3KgHa,
        // Magnesium product calculations (dolomitic Mg formula minus Ca/pH, using the Mg product)
        magnesiumRule,
        mgProductSupplyKgHa,
        magnesiumNeedKgHa,
        magnesiumYear1KgHa: finalMagnesiumYear1KgHa,
        magnesiumYear2KgHa: finalMagnesiumYear2KgHa,
        magnesiumYear3KgHa: finalMagnesiumYear3KgHa,
        // Gypsum calculations
        gypsumMeaningful,
        gypsumNeedKgHa,
        gypsumYear1KgHa: finalGypsumYear1KgHa,
        gypsumYear2KgHa: finalGypsumYear2KgHa,
        // K fertiliser calculations
        kNeedKgHa,
        kFertYear1KgHa: finalKFertYear1KgHa,
        kFertYear2KgHa: finalKFertYear2KgHa,
        // P fertiliser calculations
        pNeedKgHa,
        pFertYear1KgHa: finalPFertYear1KgHa,
        pFertYear2KgHa: finalPFertYear2KgHa,
        // Elemental Sulphur calculations
        phOperator,
        elementalSulphurKgHa: finalElementalSulphurKgHa,
        // Nutrient Inputs (mg/kg added)
        caInputMgKg,
        mgInputMgKg,
        kInputMgKg,
        pInputMgKg,
        // New calculated nutrient levels (mg/kg) - Year 1
        newCaMgKg,
        newMgMgKg,
        newKMgKg,
        newPMgKg,
        newNaMgKg,
        // New CEC and ratios
        newCec,
        newCaMgRatio,
        // Saturation before (% - same as existing percentages)
        satBeforeCa: caPercent,
        satBeforeMg: mgPercent,
        satBeforeK: kPercent,
        satBeforeNa: naPercent,
        // Saturation after (%) - Year 1
        satAfterCa: newCaPercent,
        satAfterMg: newMgPercent,
        satAfterK: newKPercent,
        satAfterNa: newNaPercent,
        // FIX 13: Year 2 predictions
        newCaMgKgY2,
        newMgMgKgY2,
        newKMgKgY2,
        newPMgKgY2,
        newNaMgKgY2,
        newCecY2,
        newCaMgRatioY2,
        satAfterCaY2: newCaPercentY2,
        satAfterMgY2: newMgPercentY2,
        satAfterKY2: newKPercentY2,
        satAfterNaY2: newNaPercentY2
    }
}

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Calculate lab info for all rows
 */
export function calculateLabInfo(rows: LabInputRow[], template: TemplateData, options?: CalcOptions): LabInfoRow[] {
    return rows.map(row => calculateLabInfoRow(row, template, options))
}

/**
 * Convert LimeProgramLabInputRow to LabInputRow format for calculations.
 * This handles the conversion from stored program data to the format needed by calculation functions.
 * Extracts metric values from NullableMeasureValue fields (mg/kg for soil data).
 */
export function convertToLabInputRow(row: LimeProgramLabInputRow): LabInputRow {
    // Extract metric values from NullableMeasureValue fields
    const ca = row.ca.metric ?? 0
    const mg = row.mg.metric ?? 0
    const k = row.k.metric ?? 0
    const na = row.na.metric ?? 0
    const pBrayI = row.pBrayI.metric ?? 0
    const sulfur = row.sulfur.metric ?? 0
    // All three exch-acid variants feed the same calculation logic; resolve the effective value
    // here so every calc path uses it. LabInputRow carries this single resolved exchH.
    const exchH = resolveExchAcid(row)

    const caCmolc = ca / ELEMENT_SPECIFIC_GRAVITY.Ca
    const mgCmolc = mg / ELEMENT_SPECIFIC_GRAVITY.Mg
    const kCmolc = k / ELEMENT_SPECIFIC_GRAVITY.K
    const naCmolc = na / ELEMENT_SPECIFIC_GRAVITY.Na
    const calculatedCec = caCmolc + mgCmolc + kCmolc + naCmolc + exchH

    const cec = row.cec ?? calculatedCec

    const caPercent = row.caPercent ?? (cec > 0 ? (caCmolc / cec) * 100 : 0)
    const mgPercent = row.mgPercent ?? (cec > 0 ? (mgCmolc / cec) * 100 : 0)
    const kPercent = row.kPercent ?? (cec > 0 ? (kCmolc / cec) * 100 : 0)
    const naPercent = row.naPercent ?? (cec > 0 ? (naCmolc / cec) * 100 : 0)

    const caMgRatio = row.caMgRatio ?? (mgPercent > 0 ? caPercent / mgPercent : 0)
    const caMgKRatio = row.caMgKRatio ?? (kPercent > 0 ? (caPercent + mgPercent) / kPercent : 0)
    const mgKRatio = row.mgKRatio ?? (kPercent > 0 ? mgPercent / kPercent : 0)
    const naKRatio = row.naKRatio ?? (kPercent > 0 ? naPercent / kPercent : 0)

    const sValue = row.sValue ?? (caCmolc + mgCmolc + kCmolc + naCmolc)
    const acidSat = row.acidSat ?? (cec > 0 ? (exchH / cec) * 100 : 0)

    return {
        referenceNr: row.referenceNr,
        // Raw pH readings carried through; the calc resolves the effective pH at point of use.
        phH2O: row.phH2O,
        phKcl: row.phKcl,
        pBrayI,
        pBrayII: row.pBrayII.metric ?? 0,
        pOlsen: row.pOlsen.metric ?? 0,
        pMehlichIII: row.pMehlichIII.metric ?? 0,
        k,
        na,
        ca,
        mg,
        fe: row.fe.metric ?? 0,
        mn: row.mn.metric ?? 0,
        zn: row.zn.metric ?? 0,
        cu: row.cu.metric ?? 0,
        exchH,
        sulfur,
        density: row.density,
        caPercent,
        mgPercent,
        kPercent,
        naPercent,
        acidSat,
        caMgRatio,
        caMgKRatio,
        mgKRatio,
        naKRatio,
        sValue,
        cec,
        // Pass through override fields
        limeTypeOverride: row.limeTypeOverride ?? null,
        limeNeedOverride: row.limeNeedOverride ?? null,
        magnesiumNeedOverride: row.magnesiumNeedOverride ?? null,
        gypsumNeedOverride: row.gypsumNeedOverride ?? null,
        kNeedOverride: row.kNeedOverride ?? null,
        pNeedOverride: row.pNeedOverride ?? null,
        elementalSulphurOverride: row.elementalSulphurOverride ?? null
    }
}

/**
 * Calculate recommendation rows for a land's lab inputs using template data.
 * FIX 3: Now accepts CalcOptions with product compositions and separate depths.
 */
export function calculateLandRows(labInputs: LimeProgramLabInputs, templateData: TemplateData, options?: CalcOptions): LimePlayFieldRow[] {
    return labInputs.rows.map(row => {
        const labInput = convertToLabInputRow(row)
        return calculateLabInfoRow(labInput, templateData, options)
    })
}

/**
 * Extract calculated output fields from a LimePlayFieldRow.
 * Use this to merge calculated values into a LimeProgramLabInputRow for saving.
 */
export function extractCalculatedOutputs(calc: LimePlayFieldRow): LimeCalculatedOutputs {
    const {
        limeType, amendmentNeedKgHa, appliedYear1KgHa, appliedYear2KgHa, appliedYear3KgHa,
        magnesiumNeedKgHa, magnesiumYear1KgHa, magnesiumYear2KgHa, magnesiumYear3KgHa,
        gypsumNeedKgHa, gypsumYear1KgHa, gypsumYear2KgHa,
        kNeedKgHa, kFertYear1KgHa, kFertYear2KgHa,
        pNeedKgHa, pFertYear1KgHa, pFertYear2KgHa,
        elementalSulphurKgHa, depthFactor, caSupplyKgHa, mgSupplyKgHa,
        caDiffMgKg, mgDiffMgKg, kDiffMgKg,
        newCaMgKg, newMgMgKg, newKMgKg, newPMgKg, newCaMgRatio,
        satAfterCa, satAfterMg, satAfterK, satAfterNa,
        newCaMgKgY2, newMgMgKgY2, newKMgKgY2, newPMgKgY2, newCaMgRatioY2,
        satAfterCaY2, satAfterMgY2, satAfterKY2, satAfterNaY2
    } = calc

    return {
        limeType, amendmentNeedKgHa, appliedYear1KgHa, appliedYear2KgHa, appliedYear3KgHa,
        magnesiumNeedKgHa, magnesiumYear1KgHa, magnesiumYear2KgHa, magnesiumYear3KgHa,
        gypsumNeedKgHa, gypsumYear1KgHa, gypsumYear2KgHa,
        kNeedKgHa, kFertYear1KgHa, kFertYear2KgHa,
        pNeedKgHa, pFertYear1KgHa, pFertYear2KgHa,
        elementalSulphurKgHa, depthFactor, caSupplyKgHa, mgSupplyKgHa,
        caDiffMgKg, mgDiffMgKg, kDiffMgKg,
        newCaMgKg, newMgMgKg, newKMgKg, newPMgKg, newCaMgRatio,
        satAfterCa, satAfterMg, satAfterK, satAfterNa,
        newCaMgKgY2, newMgMgKgY2, newKMgKgY2, newPMgKgY2, newCaMgRatioY2,
        satAfterCaY2, satAfterMgY2, satAfterKY2, satAfterNaY2
    }
}
